import { useState } from 'react';
import { Badge, Button, Input, Select } from '../../components/ui';
import { GATE_INVOICE, GATE_SHOPIFY, gateBadge, stepDone } from './financialGate';
import { cgErrorMessage, daysToPay, todayIso } from './types';

import {
  useCgStatuses,
  useClientApproved,
  useGateState,
  useLinkOrderToSupplier,
  useMarkInvoicePaid,
  useMarkInvoiceSent,
  useOrderSupplier,
  useSetGateStep,
  useSupplierOptions,
} from './hooks';
import type { CgOrder } from './types';

/**
 * The money gate and the supplier it is guarding a PO to.
 *
 * ── Why this is its own file ───────────────────────────────────────────────
 *
 * It was JSX inside the order drawer, and the drawer stopped being the way
 * people open an order — the job page is. Kyu, looking at the page: "It only
 * shows Invoice (sent/paid) but does not have the button or tickbox for
 * Shopify Order Placed anymore", and "No more option to link a supplier in the
 * PO."
 *
 * Neither had been removed. Both were somewhere he no longer went.
 *
 * So it moves out whole rather than being copied. Two checklists writing the
 * same two task rows would be two places to fix the day the gate changes, and
 * the gate is the rule the whole PO flow hangs off.
 *
 * ── It fetches its own state ───────────────────────────────────────────────
 *
 * Deliberately, so a caller only needs the order. The drawer already holds
 * these queries for its own dropdown logic and react-query dedupes them by
 * key, so the second mount costs a cache read rather than a round trip.
 */
export function FinancialGatePanel({
  order,
  onError,
}: {
  order: CgOrder;
  /** Refusals surface where the caller puts its errors, not in two places. */
  onError: (message: string) => void;
}) {
  const gate = useGateState(order.id);
  const statuses = useCgStatuses();
  const supplier = useOrderSupplier(order.id);
  const approved = useClientApproved(order.id);
  const setGateStep = useSetGateStep();
  const markPaid = useMarkInvoicePaid();

  const steps = gate.data ?? [];
  const badge = gateBadge(order.status, steps, statuses.data ?? []);
  // Three states, not two. `undefined` means "we do not know yet" — still
  // loading, or the lookup failed — and only a definite answer is stated.
  const supplierLinked = supplier.isPending ? undefined : supplier.data !== null;
  const clientApproved = approved.isPending ? undefined : approved.data;

  return (
    <div className="cg-gate-checklist">
      <div className="cg-gate-checklist-head">
        <h4 className="cg-gate-checklist-title">Financial gate</h4>
        {badge === null ? (
          <Badge tone="success">Cleared</Badge>
        ) : (
          <Badge tone="warning">{badge.label}</Badge>
        )}
      </div>

      <ol className="cg-gate-steps">
        <li className="cg-gate-step">
          <label className="cg-gate-step-label">
            <input
              type="checkbox"
              checked={stepDone(steps, GATE_SHOPIFY)}
              disabled={setGateStep.isPending}
              aria-label="Shopify Order Created"
              onChange={(event) =>
                setGateStep.mutate(
                  { orderId: order.id, slug: GATE_SHOPIFY, done: event.target.checked },
                  { onError: (err) => onError(cgErrorMessage(err.message)) },
                )
              }
            />
            <span>Shopify Order Created</span>
          </label>
          {/* The Shopify order number, not a second field holding it.
              cg_orders.order_no IS that number — the create form autofocuses it
              so the first keystroke is the id being pasted. A separate column
              would be two places for one fact. */}
          <span className="cg-gate-step-note">
            Shopify Order ID: <strong>{order.order_no}</strong>
          </span>
        </li>

        {/* Between the Shopify order and the money arriving, because that is
            when it happens: the order is raised, the invoice goes OUT, the
            client pays it. Kyu: "Invoice sent, in chronological logic is
            actually in between Shopify Order Created and Client Invoice paid."
            It had its own section above the gate, which broke the story.

            Not a checkbox, deliberately — sending an invoice is not a gate
            step and must not read as progress through the money check. A date,
            because that is all it is, and the date is what makes time-to-pay
            a subtraction. */}
        <li className="cg-gate-step">
          <span className="cg-gate-step-label">
            <span>Client Invoice Sent</span>
          </span>
          <InvoiceSentControl order={order} onError={onError} />
        </li>

        <li className="cg-gate-step">
          <label className="cg-gate-step-label">
            <input
              type="checkbox"
              checked={stepDone(steps, GATE_INVOICE)}
              disabled={setGateStep.isPending}
              aria-label="Client Invoice Paid"
              onChange={(event) =>
                setGateStep.mutate(
                  { orderId: order.id, slug: GATE_INVOICE, done: event.target.checked },
                  { onError: (err) => onError(cgErrorMessage(err.message)) },
                )
              }
            />
            <span>Client Invoice Paid</span>
          </label>
          {stepDone(steps, GATE_INVOICE) ? (
            <span className="cg-gate-step-note">
              Paid {order.date_paid ?? 'date not recorded'}
              {daysToPay(order) === null
                ? ''
                : ` · client took ${String(daysToPay(order))} day${daysToPay(order) === 1 ? '' : 's'}`}
            </span>
          ) : (
            // Ticks the step and stamps date_paid in ONE transaction, so the
            // gate and the date can never disagree about whether money landed.
            <Button
              size="sm"
              variant="secondary"
              disabled={markPaid.isPending}
              onClick={() =>
                markPaid.mutate(order.id, {
                  onError: (err) => onError(cgErrorMessage(err.message)),
                })
              }
            >
              {markPaid.isPending ? 'Marking…' : 'Mark Invoice Paid'}
            </Button>
          )}
        </li>
      </ol>

      {/* Client approval, stated as its own fact rather than folded into the
          gate list. Until 20261024000100 an order the client had never seen
          passed the gate FASTER than one they had signed — no gate tasks meant
          nothing outstanding — and neither state was visible anywhere. */}
      <p className="cg-gate-supplier">
        {clientApproved === undefined ? (
          'Checking whether the client has signed…'
        ) : clientApproved ? (
          <>Client has signed this order off.</>
        ) : (
          <span className="cg-gate-supplier-missing">
            The client has not signed this order off. Issue a review link and send it to them —
            there is no override for this one.
          </span>
        )}
      </p>

      <SupplierLinkRow
        orderId={order.id}
        linked={supplierLinked}
        supplierName={supplier.data?.organizationName ?? ''}
        onError={onError}
      />
    </div>
  );
}

/**
 * The day the invoice went out, recorded inline in the gate's chronology.
 *
 * Compact on purpose: once recorded it is a date with a quiet Edit, not a
 * standing form. The database refuses a future date and one after the invoice
 * was paid, so a typo is caught at the row rather than surfacing later as a
 * negative time-to-pay.
 */
function InvoiceSentControl({
  order,
  onError,
}: {
  order: CgOrder;
  onError: (message: string) => void;
}) {
  const mark = useMarkInvoiceSent();
  const [editing, setEditing] = useState(false);
  // todayIso, not todayNow: a full ISO timestamp is unparseable by an
  // <input type="date">, so the field rendered blank while the state held a
  // value. Same defect Kyu found on Mark delivered.
  const [on, setOn] = useState(order.invoice_sent_on ?? todayIso());

  if (order.invoice_sent_on !== null && !editing) {
    return (
      <span className="cg-gate-step-note">
        Sent {order.invoice_sent_on}{' '}
        <button type="button" className="cg-link-button" onClick={() => setEditing(true)}>
          Edit
        </button>
      </span>
    );
  }

  return (
    <span className="cg-gate-sent-control">
      <Input
        label="Invoice sent on"
        labelHidden
        type="date"
        value={on}
        onChange={(event) => setOn(event.target.value)}
      />
      <Button
        size="sm"
        variant="secondary"
        disabled={mark.isPending || on === ''}
        onClick={() =>
          mark.mutate(
            { orderId: order.id, on },
            {
              onSuccess: () => setEditing(false),
              onError: (cause) => onError(cgErrorMessage(cause.message)),
            },
          )
        }
      >
        {mark.isPending ? 'Recording…' : 'Record'}
      </Button>
    </span>
  );
}

/**
 * Who the PO is going to, and the way to say so.
 *
 * 20261022000100 refused a PO with no supplier before there was any way to add
 * one from here — the only route was Supplier Exchange → Design Handoffs →
 * Generate PO, which needs an ACKNOWLEDGED handoff to already exist. An order
 * from the client-link wizard has none, so the most common kind of order met a
 * refusal with nowhere to go. A locked door with the key on the other side.
 *
 * Linking is also what fills the MANUFACTURER block on the printed sheet: the
 * name, the named contact and the address all come from the supplier record in
 * Admin, through cg_supplier_letterhead. Kyu: "then autopopulates the address
 * in the PO (coz we already put address and contact info in the supplier in
 * admin right?)" — right, and this is the link that makes it happen.
 */
export function SupplierLinkRow({
  orderId,
  linked,
  supplierName,
  onError,
}: {
  orderId: string;
  linked: boolean | undefined;
  supplierName: string;
  onError: (message: string) => void;
}) {
  const options = useSupplierOptions();
  const link = useLinkOrderToSupplier();
  const [choosing, setChoosing] = useState(false);
  const [orgId, setOrgId] = useState('');

  if (linked === undefined) {
    return <p className="cg-gate-supplier">Checking which supplier this order is linked to…</p>;
  }

  const picker = (
    <div className="cg-supplier-picker">
      <Select
        label="Supplier"
        labelHidden
        value={orgId}
        onChange={(event) => setOrgId(event.target.value)}
        options={[
          { value: '', label: 'Choose a supplier…' },
          ...(options.data ?? []).map((org) => ({ value: org.id, label: org.name })),
        ]}
      />
      <Button
        size="sm"
        disabled={orgId === '' || link.isPending}
        onClick={() => {
          link.mutate(
            { orderId, organizationId: orgId },
            {
              onSuccess: () => {
                setChoosing(false);
                setOrgId('');
              },
              onError: (err) => onError(cgErrorMessage(err.message)),
            },
          );
        }}
      >
        {link.isPending ? 'Linking…' : 'Link supplier'}
      </Button>
      {!linked ? null : (
        <Button variant="secondary" size="sm" onClick={() => setChoosing(false)}>
          Cancel
        </Button>
      )}
    </div>
  );

  if (!linked) {
    return (
      <div className="cg-gate-supplier">
        <p className="cg-gate-supplier-missing">
          No supplier linked yet, so there is nobody to send the PO to. Choose one below — their
          address and contact from Admin fill the MANUFACTURER block on the sheet.
        </p>
        {picker}
      </div>
    );
  }

  return (
    <div className="cg-gate-supplier">
      <p>
        Going to <strong>{supplierName}</strong>.{' '}
        {choosing ? null : (
          <button type="button" className="cg-link-button" onClick={() => setChoosing(true)}>
            Change supplier
          </button>
        )}
      </p>
      {choosing ? picker : null}
    </div>
  );
}
